import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import Service from './service'
import { initialProcessState, isLivePhase, isTerminalPhase, processReducer } from './processEvents'

// How often a batch being processed without a stream on this page is read again.
export const POLL_MS = 5000

const runningOn = (batch) => batch?.status === 'processing' && !batch.processing?.stale

/**
 * The Process run of the batch page, one at a time (processEvents.js). The
 * modal is only a view of it: hiding the modal never stops a run, and leaving
 * the page only stops READING it (the server goes on; back on the page, the
 * batch says processing and the page follows it).
 *
 *   batch      the page's batch (GET /:batchId)
 *   loadBatch  reads it again: ({ quiet }) => Promise<batch | null>; quiet:
 *              a failed read keeps the page as it is
 *   onSettled  a run ended, or a run this page did not stream finished: read
 *              the rows and the file grid again (the batch is read here)
 *
 * Polls GET /:batchId every POLL_MS while this page's run is being watched
 * (its stream is gone) or while the batch is processing without a stream
 * here (another tab or user).
 */
export default function useProcessRun({ batchId, batch, loadBatch, onSettled }) {
  const [state, dispatch] = useReducer(processReducer, initialProcessState)
  const [visible, setVisible] = useState(false)
  const stateRef = useRef(state)
  stateRef.current = state
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled
  // The stream being read: its AbortController (null when none).
  const streamRef = useRef(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Another batch, or leaving the page: stop reading; start clean.
  useEffect(
    () => () => {
      streamRef.current?.abort()
      streamRef.current = null
      dispatch({ type: 'reset' })
      setVisible(false)
    },
    [batchId]
  )

  /** Show the modal: the run going on or its ending, else a new confirm step (the batch read again first). */
  const open = useCallback(async () => {
    setVisible(true)
    if (isLivePhase(stateRef.current.phase)) return
    dispatch({ type: 'open' })
    const fresh = await loadBatch({ quiet: true })
    if (mounted.current) dispatch({ type: 'checked', ok: Boolean(fresh) })
  }, [loadBatch])

  const start = useCallback(async () => {
    const current = stateRef.current
    if (current.phase !== 'confirm' || current.checking || streamRef.current) return
    const controller = new AbortController()
    streamRef.current = controller
    const mine = () => streamRef.current === controller
    dispatch({ type: 'begin' })
    try {
      await Service.process(
        batchId,
        (event) => {
          if (mine()) dispatch({ type: 'event', event })
        },
        { signal: controller.signal }
      )
      if (mine()) dispatch({ type: 'ended' })
    } catch (error) {
      if (mine() && error?.name !== 'AbortError') dispatch({ type: 'rejected', error })
    } finally {
      if (mine()) streamRef.current = null
    }
  }, [batchId])

  const show = useCallback(() => setVisible(true), [])
  const hide = useCallback(() => setVisible(false), [])
  /** Close the modal; an ending (or the confirm step) is done with, a live run only hides. */
  const close = useCallback(() => {
    setVisible(false)
    dispatch({ type: 'close' })
  }, [])

  // Every ending, once per run: the page reads everything again.
  const handled = useRef(null)
  useEffect(() => {
    if (!isTerminalPhase(state.phase) || handled.current === state.seq) return
    handled.current = state.seq
    loadBatch({ quiet: true })
    onSettledRef.current?.()
  }, [state.phase, state.seq, loadBatch])

  // Polling: this page's run while its stream is gone, or a run elsewhere.
  const watching = state.phase === 'watching'
  const elsewhere = runningOn(batch) && !isLivePhase(state.phase)
  const polling = watching || elsewhere
  useEffect(() => {
    if (!polling) return undefined
    let active = true
    const timer = setInterval(async () => {
      const fresh = await loadBatch({ quiet: true })
      if (!active || !fresh) return
      dispatch({ type: 'polled', batch: fresh })
      // A run this page does not stream finished: the rows changed too.
      if (!runningOn(fresh) && !isLivePhase(stateRef.current.phase)) onSettledRef.current?.()
    }, POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [polling, loadBatch])

  return { state, visible, open, start, show, hide, close, polling, elsewhere }
}
